'use client';

import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { PaymentIntent, StripePaymentElementOptions } from '@stripe/stripe-js';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import Button from '@cineamo/ui/src/button/NewButton';
import { IconLoading16 } from '@cineamo/ui/src/icons/ic-loading';
import ErrorLabel from '@cineamo/ui/src/label/ErrorLabel';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

const PaymentFormView: React.FC<{
    paymentStatusMessage: string | null;
    clientSecret: string;
    onPaymentComplete: () => void;
    submitTrigger: number;
}> = ({ paymentStatusMessage, clientSecret, onPaymentComplete, submitTrigger }) => {
    const stripe = useStripe();
    const elements = useElements();
    const t = useTranslations('tickets');
    const { track } = useAnalytics();

    const { total, currency, isProcessing: isLoading, setIsProcessing: setIsLoading } = useCartProvider();

    const [message, setMessage] = useState(paymentStatusMessage);

    const submitAttempt = useRef(0);
    const formRef = useRef<HTMLFormElement>(null);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const paymentElementOptions: StripePaymentElementOptions = {
        // The first item opens preselected. Stripe hides a wallet on devices that cannot use it, so
        // Apple Pay / Google Pay lead where available and card leads everywhere else.
        layout: { type: 'accordion', defaultCollapsed: false },
        // Stripe shows listed methods first and appends unlisted ones after them, so a method newly
        // enabled in the Dashboard lands behind PayPal until it is added here.
        paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'amazon_pay', 'paypal']
    };

    const startPaymentIntentStatusCheckerInterval = useCallback(
        (paymentIntent?: PaymentIntent) => {
            intervalRef.current = setInterval(async () => {
                if (!paymentIntent) return;
                switch (paymentIntent.status) {
                    case 'succeeded':
                        onPaymentComplete();
                        setIsLoading(false);
                        if (intervalRef.current) clearInterval(intervalRef.current);
                        break;
                    case 'requires_capture':
                        paymentIntent =
                            (await stripe?.retrievePaymentIntent(clientSecret))?.paymentIntent ?? paymentIntent;
                        break;
                    default:
                        break;
                }
            }, 1000);
        },
        [onPaymentComplete, setIsLoading, stripe, clientSecret]
    );

    const handleSubmit = useCallback(async () => {
        if (!formRef.current?.reportValidity()) {
            return;
        }

        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setIsLoading(true);

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements: elements,
            confirmParams: {
                // Return to this URL if the payment was successful.
                return_url: window.location.origin + window.location.pathname
            },
            redirect: 'if_required'
        });

        startPaymentIntentStatusCheckerInterval(paymentIntent);

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error?.message) {
            console.error('PaymentSteps.tsx confirmPayment Error:', error);
            setMessage(error.message);
            setIsLoading(false);
        }
    }, [stripe, elements, setIsLoading, startPaymentIntentStatusCheckerInterval]);

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    useEffect(() => {
        if (submitTrigger > submitAttempt.current && !isLoading) {
            submitAttempt.current = submitTrigger;
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: triggered by parent via submitTrigger prop to initiate Stripe payment submission
            handleSubmit();
        }
    }, [submitTrigger, handleSubmit, isLoading]);

    const formatter = useFormat();
    const amount = formatter.number.formatCurrency(total / 100, currency);
    const currentPaymentMethod = useRef<string | null>(null);

    return (
        <div className="max-w-[520px] lg:max-w-[680px] mx-auto">
            <form
                id="payment-form"
                className="flex flex-col space-y-12 lg:space-y-32 lg:my-32"
                ref={formRef}
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}>
                <div className="py-12 px-9 lg:p-0 flex flex-col space-y-12">
                    <h3 className="text-body-bold-16">{t('payment.payment-method.title')}</h3>
                    <PaymentElement
                        id="payment-element"
                        options={paymentElementOptions}
                        onChange={(e) => {
                            if (currentPaymentMethod.current !== e.value.type) {
                                currentPaymentMethod.current = e.value.type;
                                track('Payment Method Changed', {
                                    paymentMethod: e.value.type
                                });
                            }
                        }}
                    />
                </div>

                <Button
                    className="w-full h-48 hidden lg:flex"
                    type="submit"
                    disabled={isLoading || !stripe || !elements}
                    leadingIcon={isLoading && <IconLoading16 className="animate-spin" />}>
                    {t('pay-now', { amount })}
                </Button>

                {/* Show any error or success messages */}
                {message && <ErrorLabel error={message} />}
            </form>
        </div>
    );
};

export default PaymentFormView;
